// Re-authentication for administrator actions (docs/SECURITY.md): the server refuses security
// changes (users, roles, resets, the 2FA requirement) with 403 `reauth_required` unless this
// session confirmed the password (and code) in the last 10 minutes. useReauth() runs such an action;
// on that answer it asks for the password and code, re-authenticates and runs it once more.
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { isReauthRequired, reauthenticate } from '../lib/account';
import { accountErrorText, CodeInput, codeLooksComplete, FormError, PasswordInput } from './AccountForms';
import { Modal } from './Dialog';
import { Icon } from './Icon';

/** The user closed the re-authentication without confirming: the action was not done. */
export class ReauthCancelled extends Error {
  constructor() {
    super('Dejanje je preklicano.');
    this.name = 'ReauthCancelled';
  }
}

export function ReauthDialog({ needsCode, onDone, onCancel }: { needsCode: boolean; onDone: () => void; onCancel: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = password !== '' && (!needsCode || codeLooksComplete(code, true));

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await reauthenticate({ password, code: needsCode ? code : undefined });
      onDone();
    } catch (err) {
      setError(accountErrorText(err, 'Potrditev ni uspela.'));
      setCode('');
      setBusy(false);
    }
  };

  return (
    <Modal title="Potrdite, da ste to vi" onClose={busy ? undefined : onCancel}>
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p>
          Za spremembe uporabnikov in varnostnih nastavitev znova vnesite svoje geslo
          {needsCode ? ' in kodo iz aplikacije za preverjanje' : ''}. Potrditev velja 10 minut.
        </p>
        <PasswordInput id="reauth-password" label="Vaše geslo" value={password} onChange={setPassword} autoComplete="current-password" disabled={busy} />
        {needsCode && (
          <CodeInput
            id="reauth-code"
            label="Koda iz aplikacije za preverjanje (ali obnovitvena koda)"
            value={code}
            onChange={setCode}
            allowRecovery
            disabled={busy}
          />
        )}
        {error && <FormError>{error}</FormError>}
        <div className="form-actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={busy}>
            Prekliči
          </button>
          <button type="submit" className="btn btn--primary" disabled={!ready || busy}>
            <Icon name={busy ? 'spinner' : 'check'} size={20} /> Potrdi
          </button>
        </div>
      </form>
    </Modal>
  );
}

export interface Reauth {
  /**
   * Runs `action`; when the server asks for a re-authentication, shows the dialog and, once the
   * user confirmed, runs it once more. Rejects with ReauthCancelled when the user cancels.
   */
  guard: <T>(action: () => Promise<T>) => Promise<T>;
  /** Render this where the section renders its dialogs. */
  dialog: ReactNode;
}

/** `needsCode`: the signed-in user has 2FA, so the code is asked too. */
export function useReauth(needsCode: boolean): Reauth {
  const [open, setOpen] = useState(false);
  const pending = useRef<((confirmed: boolean) => void) | null>(null);

  const ask = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        pending.current = resolve;
        setOpen(true);
      }),
    [],
  );
  const finish = (confirmed: boolean) => {
    setOpen(false);
    const resolve = pending.current;
    pending.current = null;
    resolve?.(confirmed);
  };

  const guard = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T> => {
      try {
        return await action();
      } catch (err) {
        if (!isReauthRequired(err)) throw err;
        if (!(await ask())) throw new ReauthCancelled();
        return action();
      }
    },
    [ask],
  );

  return {
    guard,
    dialog: open ? <ReauthDialog needsCode={needsCode} onDone={() => finish(true)} onCancel={() => finish(false)} /> : null,
  };
}
